import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getSafetyInspectionResults } from "../../../api/hse/safetyInspections";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { ResourceError } from "../../utils/ResourceError";
import { PMButton } from "../../utils/PMButton";

import { DocumentManagerV6 } from "../Media/DocumentManagerV6";
import {
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";

export const HseSafetyInspectionSummary = ({
  inspection,
  onClose,
  showClose = true,
  showHeader = true,
}) => {
  const { t } = useTranslation();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  // Fetch results
  const fetchResults = async () => {
    setLoading(true);
    try {
      const response = await getSafetyInspectionResults(inspection.id);
      setResults(response.data.data);
    } catch (err) {
      console.error("Error fetching results:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResults();
  }, [inspection.id]);

  // Statistics
  const totalCheckpoints = results.length;
  const completedCheckpoints = results.filter(
    (r) => r.status !== "not_checked"
  ).length;
  const deviationCount = results.filter((r) => r.status === "deviation").length;
  const needsReviewCount = results.filter(
    (r) => r.status === "needs_review"
  ).length;
  const okCount = results.filter((r) => r.status === "ok").length;
  const notApplicableCount = results.filter(
    (r) => r.status === "not_applicable"
  ).length;
  const notCheckedCount = results.filter(
    (r) => r.status === "not_checked"
  ).length;

  // Group results by area
  const resultsByArea = results.reduce((acc, result) => {
    const areaTitle =
      result.hse_safety_checkpoint?.hse_safety_area?.title || "Ukjent område";
    if (!acc[areaTitle]) {
      acc[areaTitle] = [];
    }
    acc[areaTitle].push(result);
    return acc;
  }, {});

  // Filter results by status
  const deviations = results.filter((r) => r.status === "deviation");
  const needsReview = results.filter((r) => r.status === "needs_review");
  const allResults = results;

  if (loading) {
    return <ResourceLoading />;
  }

  if (error) {
    return <ResourceError message={error} />;
  }

  const renderCheckpoint = (result) => (
    <div key={result.id} className="border rounded-lg p-4 mb-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <h4 className="font-medium text-gray-900">
              {result.hse_safety_checkpoint?.title}
            </h4>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                result.status === "not_checked"
                  ? "bg-gray-100 text-gray-800"
                  : result.status === "ok"
                  ? "bg-green-100 text-green-800"
                  : result.status === "deviation"
                  ? "bg-red-100 text-red-800"
                  : result.status === "needs_review"
                  ? "bg-yellow-100 text-yellow-800"
                  : result.status === "not_applicable"
                  ? "bg-gray-100 text-gray-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {result.status_display}
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-2">
            {result.hse_safety_checkpoint?.hse_safety_area?.title}
          </p>
          {result.hse_safety_checkpoint?.description && (
            <p className="text-sm text-gray-700 mb-2">
              {result.hse_safety_checkpoint.description}
            </p>
          )}
          {result.comment && (
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-sm text-gray-700">{result.comment}</p>
            </div>
          )}
          {result.images && result.images.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-medium text-gray-700 mb-2">
                {t("Bilder")}:
              </p>
              <div className="flex space-x-2">
                {result.images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Bilde ${index + 1}`}
                    className="w-16 h-16 object-cover rounded border"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: "overview", label: t("Oversikt"), icon: InformationCircleIcon },
    {
      id: "deviations",
      label: t("Avvik"),
      icon: XMarkIcon,
      count: deviationCount,
    },
    {
      id: "needs_review",
      label: t("Må vurderes"),
      icon: ExclamationTriangleIcon,
      count: needsReviewCount,
    },
    {
      id: "all",
      label: t("Alle sjekkpunkter"),
      icon: CheckIcon,
      count: totalCheckpoints,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        {showHeader ? (
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {inspection.title}
            </h2>
            <p className="text-gray-600">{inspection.location}</p>
            <p className="text-sm text-gray-500">
              {new Date(inspection.inspection_date).toLocaleDateString("nb-NO")}
            </p>
          </div>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              inspection.status === "draft"
                ? "bg-gray-100 text-gray-800"
                : inspection.status === "in_progress"
                ? "bg-blue-100 text-blue-800"
                : inspection.status === "completed"
                ? "bg-green-100 text-green-800"
                : inspection.status === "closed"
                ? "bg-gray-100 text-gray-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {inspection.status_display}
          </span>
        </div>
        ) : null}

        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">
              {t("Fremdrift")}
            </span>
            <span className="text-sm text-gray-500">
              {completedCheckpoints}/{totalCheckpoints} {t("fullført")}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full"
              style={{
                width: `${(completedCheckpoints / totalCheckpoints) * 100}%`,
              }}
            ></div>
          </div>
          <span className="text-xs text-gray-500">
            {completedCheckpoints}/{totalCheckpoints}
          </span>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-5 gap-4 text-sm">
          <div className="text-center">
            <div className="font-semibold text-blue-600">{okCount}</div>
            <div className="text-gray-500">{t("OK")}</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-red-600">{deviationCount}</div>
            <div className="text-gray-500">{t("Avvik")}</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-yellow-600">
              {needsReviewCount}
            </div>
            <div className="text-gray-500">{t("Må vurderes")}</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-gray-600">
              {notApplicableCount}
            </div>
            <div className="text-gray-500">{t("Ikke aktuelt")}</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-gray-400">{notCheckedCount}</div>
            <div className="text-gray-500">{t("Ikke sjekket")}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                  {tab.count !== undefined && (
                    <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {t("Resultater per område")}
                </h3>
                {Object.entries(resultsByArea).map(
                  ([areaTitle, areaResults]) => {
                    const areaOkCount = areaResults.filter(
                      (r) => r.status === "ok"
                    ).length;
                    const areaDeviationCount = areaResults.filter(
                      (r) => r.status === "deviation"
                    ).length;
                    const areaNeedsReviewCount = areaResults.filter(
                      (r) => r.status === "needs_review"
                    ).length;
                    const areaCompletedCount = areaResults.filter(
                      (r) => r.status !== "not_checked"
                    ).length;

                    return (
                      <div
                        key={areaTitle}
                        className="border rounded-lg p-4 mb-4"
                      >
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="font-medium text-gray-900">
                            {areaTitle}
                          </h4>
                          <div className="flex space-x-2 text-sm">
                            <span className="text-blue-600">
                              {areaOkCount} OK
                            </span>
                            <span className="text-red-600">
                              {areaDeviationCount} Avvik
                            </span>
                            <span className="text-yellow-600">
                              {areaNeedsReviewCount} Må vurderes
                            </span>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{
                              width: `${
                                (areaCompletedCount / areaResults.length) * 100
                              }%`,
                            }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-500">
                          {areaCompletedCount}/{areaResults.length}
                        </span>
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          )}

          {/* Deviations Tab */}
          {activeTab === "deviations" && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("Avvik")} ({deviationCount})
              </h3>
              {deviations.length > 0 ? (
                deviations.map(renderCheckpoint)
              ) : (
                <p className="text-gray-500 italic">
                  {t("Ingen avvik funnet")}
                </p>
              )}
            </div>
          )}

          {/* Needs Review Tab */}
          {activeTab === "needs_review" && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("Må vurderes")} ({needsReviewCount})
              </h3>
              {needsReview.length > 0 ? (
                needsReview.map(renderCheckpoint)
              ) : (
                <p className="text-gray-500 italic">
                  {t("Ingen punkter som må vurderes")}
                </p>
              )}
            </div>
          )}

          {/* All Checkpoints Tab */}
          {activeTab === "all" && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("Alle sjekkpunkter")} ({totalCheckpoints})
              </h3>
              {allResults.map(renderCheckpoint)}
            </div>
          )}
        </div>
      </div>

      {/* Documents */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t("Dokumenter")}
        </h3>
        <DocumentManagerV6
          module="hse_safety_inspection"
          moduleParam={inspection.id}
          mode="dropzone"
          allowCopy={false}
          allowMove={false}
        />
      </div>

      {showClose && onClose ? (
      <div className="flex justify-end">
        <PMButton onClick={onClose} variant="outline">
          {t("Lukk")}
        </PMButton>
      </div>
      ) : null}
    </div>
  );
};
